import { useMemo } from "react";
import { useQuery, type QueryState } from "../analytics";
import { workspaceKeyPrefix } from "../identity/active";
import { useConfigClient } from "./client";
import { mergeSchema, type EffectiveSchema } from "./schema";
import type { ConfigProfile } from "./types";

/* Reads use the analytics useQuery cache, keyed under the active workspace
 * (the gateway scopes configs by X-Workspace-Id). Mutations don't go through
 * here — callers invoke the client directly then bump `version` to force a
 * refetch (a new cache key), so the list reflects the change without a full
 * reload. */

export function useConfigs(version = 0): QueryState<ConfigProfile[]> {
  const client = useConfigClient();
  return useQuery(
    () => client.list(),
    `${workspaceKeyPrefix()}configs:v${version}`,
    "loadConfigs",
  );
}

export function useConfig(
  configId: string,
  version = 0,
): QueryState<ConfigProfile> {
  const client = useConfigClient();
  return useQuery(
    () => client.get(configId),
    `${workspaceKeyPrefix()}config:${configId}:v${version}`,
    "loadConfigs",
    // No config_id yet (agent has no bound profile) → nothing to read. Skip the
    // request so we don't hit GET /settings/configs/ with an empty id, which
    // would fire a bogus loadConfigs call (and a spurious ApiRequestFailed).
    // The editor seeds from schema defaults until first save creates one.
    Boolean(configId),
  );
}

export interface ConfigSchemaState {
  /** Gateway-resolved defaults overlaid on the static metadata (or the static
   *  fallback until the fetch settles / if it fails). */
  schema: EffectiveSchema;
  /** True once the schema fetch has settled (loaded or errored→fallback), so
   *  callers can defer seeding default values until they're authoritative. */
  ready: boolean;
}

/** The overridable-config schema, with the deployment's resolved defaults
 *  overlaid on the UI's presentation metadata. Process-wide cached and deduped
 *  via useQuery, so every editor surface shares one fetch. */
export function useConfigSchema(): ConfigSchemaState {
  const client = useConfigClient();
  const q = useQuery(() => client.getSchema(), "config:schema");
  const schema = useMemo(() => mergeSchema(q.data ?? null), [q.data]);
  return { schema, ready: q.data !== undefined || q.error !== undefined };
}
